import {
  Badge,
  Box,
  Button,
  CircularProgress,
  CircularProgressLabel,
  Flex,
  HStack,
  Icon,
  Stat,
  StatArrow,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { CheckCircle, Target, TrendingUp } from 'lucide-react';
import { FC, useState } from 'react';
import { ValidationComparisonData } from '../utils/validation.utils';

interface AIComparisonMetricsProps {
  comparisonData: ValidationComparisonData;
}

export const AIComparisonMetrics: FC<AIComparisonMetricsProps> = ({
  comparisonData,
}) => {
  const totalAiIssues =
    comparisonData.aiErrorCount + comparisonData.aiWarningCount;
  const totalRegularIssues =
    comparisonData.regularErrorCount + comparisonData.regularWarningCount;
  const issuesReduced = totalRegularIssues - totalAiIssues;

  return (
    <VStack align="stretch" spacing={4}>
      <Text color="compliance.debug.text" fontSize="sm">
        AI validation compared to regular validation
      </Text>

      {/* Summary Metrics */}
      <HStack spacing={6}>
        <Box>
          <CircularProgress
            color={
              comparisonData.reductionPercentage > 0 ? 'green.400' : 'red.400'
            }
            size="60px"
            value={Math.abs(comparisonData.reductionPercentage)}
          >
            <CircularProgressLabel fontSize="xs" fontWeight="bold">
              {comparisonData.reductionPercentage}%
            </CircularProgressLabel>
          </CircularProgress>
          <Text
            color="compliance.debug.text"
            fontSize="xs"
            mt={1}
            textAlign="center"
          >
            {comparisonData.reductionPercentage > 0 ? 'Improvement' : 'Change'}
          </Text>
        </Box>

        <VStack align="start">
          <Stat size="lg">
            <StatLabel color="compliance.debug.text">Issues Reduced</StatLabel>
            <StatNumber
              color={
                issuesReduced > 0
                  ? 'green.400'
                  : issuesReduced < 0
                    ? 'red.400'
                    : 'gray.400'
              }
              fontSize="lg"
            >
              {issuesReduced > 0 ? '-' : ''}
              {issuesReduced}
            </StatNumber>
            <StatHelpText bottom={-2} position="relative">
              <StatArrow
                type={
                  issuesReduced > 0
                    ? 'increase'
                    : issuesReduced < 0
                      ? 'decrease'
                      : undefined
                }
              />
              {issuesReduced > 0
                ? 'Fewer issues'
                : issuesReduced < 0
                  ? 'More issues'
                  : 'No change'}
            </StatHelpText>
          </Stat>
        </VStack>
      </HStack>

      {/* Detailed Comparison */}
      <Box bg="compliance.debug.code.bg" borderRadius="md" p={4} pt={3}>
        <Text
          color="compliance.debug.text"
          fontSize="sm"
          fontWeight="semibold"
          mb={2}
        >
          Detailed Comparison
        </Text>

        <VStack align="stretch" spacing={4}>
          {/* AI Results */}
          <Flex alignItems="flex-start" direction="column" gap={1}>
            <HStack>
              <Icon
                as={CheckCircle}
                color="compliance.diff.improved.icon"
                size={16}
              />
              <Text color="compliance.debug.text" fontSize="sm">
                AI Filtered Results
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Badge
                bg="compliance.status.error"
                color="white"
                size="sm"
                variant="solid"
              >
                {comparisonData.aiErrorCount} errors
              </Badge>
              <Badge
                bg="compliance.status.warning"
                color="white"
                size="sm"
                variant="solid"
              >
                {comparisonData.aiWarningCount} warnings
              </Badge>
            </HStack>
          </Flex>

          {/* Regular Results */}
          <Flex alignItems="flex-start" direction="column" gap={1}>
            <HStack>
              <Icon
                as={CheckCircle}
                color="compliance.status.error"
                size={16}
              />
              <Text color="compliance.debug.text" fontSize="sm">
                Regular Results
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Badge
                bg="compliance.status.error"
                color="white"
                size="sm"
                variant="solid"
              >
                {comparisonData.regularErrorCount} errors
              </Badge>
              <Badge
                bg="compliance.status.warning"
                color="white"
                size="sm"
                variant="solid"
              >
                {comparisonData.regularWarningCount} warnings
              </Badge>
            </HStack>
          </Flex>
        </VStack>
      </Box>

      {/* Improved Fields List */}
      {comparisonData.improvedFieldsList &&
        comparisonData.improvedFieldsList.length > 0 && (
          <Box bg="compliance.debug.code.bg" borderRadius="md" p={4}>
            <HStack mb={3}>
              <Icon
                as={Target}
                color="compliance.diff.improved.icon"
                size={16}
              />
              <Text
                color="compliance.debug.text"
                fontSize="sm"
                fontWeight="semibold"
              >
                Improved Fields ({comparisonData.improvedFieldsList.length})
              </Text>
            </HStack>
            <Wrap spacing={2}>
              {comparisonData.improvedFieldsList.map((field, index) => (
                <WrapItem key={index}>
                  <Badge
                    bg="compliance.diff.improved.bg"
                    color="compliance.diff.improved.text"
                    px={2}
                    py={1}
                    size="sm"
                    variant="subtle"
                  >
                    <HStack spacing={1}>
                      <Text fontSize="xs">{field.fieldLabel}</Text>
                      {field.reductionCount > 0 && (
                        <Text
                          color="compliance.diff.improved.icon"
                          fontSize="xs"
                          fontWeight="bold"
                        >
                          -{field.reductionCount}
                        </Text>
                      )}
                    </HStack>
                  </Badge>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        )}

      {/* AI Analysis */}
      {comparisonData.aiAnalysisWithFields &&
        comparisonData.aiAnalysisWithFields.length > 0 && (
          <Box bg="compliance.debug.code.bg" borderRadius="md" p={4}>
            <Text
              color="compliance.debug.text"
              fontSize="sm"
              fontWeight="semibold"
              mb={2}
            >
              AI Analysis Insights
            </Text>
            <VStack align="stretch" spacing={1}>
              {comparisonData.aiAnalysisWithFields.map(
                (analysisItem, index) => (
                  <ExpandableTextWithFields
                    key={index}
                    improvedFieldsList={comparisonData.improvedFieldsList}
                    relatedFieldKeys={analysisItem.relatedFieldKeys}
                    text={analysisItem.analysis}
                  />
                )
              )}
            </VStack>
          </Box>
        )}

      {/* Fallback for legacy AI Analysis */}
      {!comparisonData.aiAnalysisWithFields &&
        comparisonData.aiAnalysisData &&
        comparisonData.aiAnalysisData.length > 0 && (
          <Box bg="compliance.debug.code.bg" borderRadius="md" p={4}>
            <Text
              color="compliance.debug.text"
              fontSize="sm"
              fontWeight="semibold"
              mb={2}
            >
              AI Analysis Insights
            </Text>
            <VStack align="stretch" spacing={1}>
              {comparisonData.aiAnalysisData.map((analysis, index) => (
                <ExpandableText key={index} text={analysis} />
              ))}
            </VStack>
          </Box>
        )}
    </VStack>
  );
};

interface ExpandableTextProps {
  text: string;
}

const ExpandableText: FC<ExpandableTextProps> = ({ text }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <HStack align="start">
      <Icon
        as={TrendingUp}
        color="compliance.diff.improved.icon"
        flexShrink={0}
        mt={0.5}
        size={12}
      />
      <VStack align="start" spacing={1}>
        <Text
          color="compliance.debug.text"
          fontSize="sm"
          lineHeight="1.4"
          noOfLines={isExpanded ? undefined : 2}
        >
          {text}
        </Text>
        {text.length > 100 && (
          <Button
            color="compliance.debug.text"
            fontSize="xs"
            fontWeight="normal"
            h="auto"
            minH="auto"
            p={0}
            size="xs"
            variant="link"
            onClick={toggleExpanded}
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </Button>
        )}
      </VStack>
    </HStack>
  );
};

interface ExpandableTextWithFieldsProps {
  text: string;
  relatedFieldKeys?: string[];
  improvedFieldsList?: Array<{
    fieldKey: string;
    fieldLabel: string;
    reductionCount: number;
  }>;
}

const ExpandableTextWithFields: FC<ExpandableTextWithFieldsProps> = ({
  text,
  relatedFieldKeys,
  improvedFieldsList,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const getFieldLabel = (fieldKey: string): string => {
    const field = improvedFieldsList?.find((f) => f.fieldKey === fieldKey);

    return field?.fieldLabel || fieldKey;
  };

  return (
    <HStack align="start">
      <Icon
        as={TrendingUp}
        color="compliance.diff.improved.icon"
        flexShrink={0}
        mt={0.5}
        size={12}
      />
      <VStack align="start" spacing={1}>
        <Text
          color="compliance.debug.text"
          fontSize="sm"
          lineHeight="1.4"
          noOfLines={isExpanded ? undefined : 2}
        >
          {text}
        </Text>
        {relatedFieldKeys && relatedFieldKeys.length > 0 && (
          <Wrap spacing={1}>
            {relatedFieldKeys.map((fieldKey) => (
              <WrapItem key={fieldKey}>
                <Badge
                  bg="compliance.diff.improved.bg"
                  color="compliance.diff.improved.text"
                  fontSize="xs"
                  px={2}
                  py={0.5}
                  size="sm"
                  variant="outline"
                >
                  {getFieldLabel(fieldKey)}
                </Badge>
              </WrapItem>
            ))}
          </Wrap>
        )}
        {text.length > 100 && (
          <Button
            color="compliance.debug.text"
            fontSize="xs"
            fontWeight="normal"
            h="auto"
            minH="auto"
            p={0}
            size="xs"
            variant="link"
            onClick={toggleExpanded}
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </Button>
        )}
      </VStack>
    </HStack>
  );
};
